import { useParticipantProgressFilter } from './ParticipantProgressFilter';
import { Checkbox } from '@src/components/Checkbox/Checkbox';
import { BulkReminderButton } from './BulkReminder';
import { LockIcon } from '@src/components/icons/LockIcon';
import { useProgressInvitations } from './useProgressInvitations';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { MailIcon } from '@src/components/icons/MailIcon';
import { Hint } from '@src/components/Hint/Hint';
import { useParticipantPreview } from './ParticipantPreview';
import { Typography } from '@src/components/Typography/Typography';
import '@src/components/ParticipantRow/ParticipantRow.css';
import { EyeIcon } from '@src/components/icons/EyeIcon';
import { type ReactNode, useId, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { UserIcon } from '@src/components/icons/UserIcon';
import { showSheet } from '@src/components/Sheet/Sheet';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { ActivityEventIcon } from './ActivityEventIcon';
import './ParticipantProgress.tokens.css';
import './ParticipantProgress.css';

export type ProgressParticipant = { invitation?: {emailStatus: 'sent' | 'queued' | 'error' | null; linkCopied: boolean}; openMethod?: 'link' | 'email'; completion?: {answered: number; questions: number; percent: number | null}; id: number; name: string; opened: boolean; filled: boolean; revealed: boolean; sent: boolean; received: boolean };
type ChartStatus = 'opened' | 'filled' | 'revealed' | 'sent' | 'received';

function SelectionHint({ disabled, hidden = false, children }: { disabled: boolean; hidden?: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  return <span className="progress-selection-hint">
    <span style={hidden ? { visibility: 'hidden' } : undefined} aria-hidden={hidden || undefined}>{children}</span>
    {disabled && <button type="button" className="progress-selection-hint__trigger"
      aria-label={t('bulkReminder.selectionUnavailable')} aria-describedby={open ? id : undefined}
      onClick={event => { event.stopPropagation(); setOpen(true); }} onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); } }} />}
    {disabled && open && <span id={id} role="tooltip" className="participant-progress__tooltip progress-selection-hint__tooltip">{t('bulkReminder.selectionUnavailable')}</span>}
  </span>;
}

function ProgressParticipantRow({ person, card, status, box, invitations, onPreview, selected, onSelect, selectionDisabled = false }: {
  selected: boolean;
  selectionDisabled?: boolean;
  onSelect?: (checked: boolean) => void;
  person: ProgressParticipant;
  card?: CardInfo;
  status: ChartStatus;
  box: BoxInfo['box'];
  invitations: ReturnType<typeof useProgressInvitations>;
  onPreview: (card: CardInfo) => void;
}) {
  const { t } = useTranslation();
  const participation = status === 'filled';
  const pendingKey = { filled: 'notFilled', opened: 'notOpened', revealed: 'notRevealed', sent: 'notSent', received: 'notReceived' }[status];
          const completion = person.completion;
          const hasAnswers = (completion?.answered ?? (person.filled ? 1 : 0)) > 0;
          const completionStatus = hasAnswers
            ? completion?.percent === 100 || person.filled ? 'cardFullyFilled' : 'cardPercentFilled'
            : person.opened ? participation ? 'cardNotFilledYet' : 'openedNotFilled' : 'cardNotOpened';
          const statusKey = !person.opened ? 'notJoinedYet' : participation || status === 'opened'
            ? completionStatus
            : person[status] ? status === 'revealed' ? 'recipientAlreadyKnown' : status : status === 'revealed' ? 'recipientNotKnownYet' : status === 'sent' ? 'giftNotSentYet' : status === 'received' ? 'giftNotReceivedYet' : pendingKey;
          return <li onClick={event => {
            if ((event.target as HTMLElement).closest('button, input, label, a, .progress-details__select')) return;
            if (!selectionDisabled) onSelect?.(!selected);
          }} data-selectable={Boolean(onSelect) && !selectionDisabled}>{onSelect && <span className="progress-details__select"><SelectionHint disabled={selectionDisabled} hidden={selectionDisabled}><Checkbox fit disabled={selectionDisabled} checked={selected} onCheckedChange={onSelect} aria-label={t('bulkReminder.selectPerson', { name: person.name })} /></SelectionHint></span>}<span className="progress-details__avatar" aria-hidden="true">{card?.picture ? <ParticipantFace picture={card.picture} avatar={card.avatar} frameName={card.frameName} muted={!person.opened} /> : <UserIcon />}</span><div>{card ? <Typography as="span" role="title/sub" className="participant-row__name">{card.username}</Typography> : <strong>{person.name}</strong>}<span>{t(`participantProgress.${statusKey}`, { percent: completion?.percent ?? 100 })}</span>{!person.opened && <>
            {person.invitation?.emailStatus === 'error' ? <Hint tone="error">{t('participantProgress.invitationEmail_error')}</Hint> : person.invitation?.emailStatus && <span>{t(`participantProgress.invitationEmail_${person.invitation.emailStatus}`)}</span>}
            {person.invitation?.linkCopied && <span>{t('participantProgress.invitationLinkCopied')}</span>}
          </>}</div>{card && <span className="progress-details__invite-actions">
            <button className="progress-details__preview" type="button" aria-label={t('box.organizer.preview.title', { name: card.username })} title={t('box.organizer.preview.title', { name: card.username })} onClick={() => onPreview(card)}><EyeIcon /></button>
            {!person.opened && !box.isArchived && <>
            <button type="button" aria-label={t('box.organizer.menu.copy')} title={t('box.organizer.menu.copy')} onClick={() => invitations.link(card)}><LinkIcon /></button>
            <button type="button" aria-label={t('box.organizer.menu.email')} title={t('box.organizer.menu.email')} onClick={() => invitations.email(card)}><MailIcon /></button></>}
          </span>}</li>;
}

function ProgressDetails({ progress, status, cards, box, onPreview, initialFilter = 'pending' }: { progress: Progress; status: ChartStatus; cards: CardInfo[]; box: BoxInfo['box']; onPreview: (card: CardInfo) => void; initialFilter?: 'pending' | 'done' }) {
  const { t } = useTranslation();
  const invitations = useProgressInvitations(box, cards, progress.activeDraw);
  const [filter, setFilterState] = useState(initialFilter);
  const [selected, setSelected] = useState<number[]>([]);
  const setFilter = (next: 'pending' | 'done') => { setSelected([]); setFilterState(next); };
  const id = useId();
  const people = progress.participants ?? [];
  const participation = status === 'filled';
  const count = participation ? progress.engagement.filled : status === 'opened' ? progress.engagement.opened : progress[status];
  const percent = progress.total ? Math.round(count / progress.total * 100) : 0;
  const completed = (person: ProgressParticipant) => participation ? (person.completion?.answered ?? (person.filled ? 1 : 0)) > 0 : person[status];
  const matches = (person: ProgressParticipant, tab: typeof filter) => tab === 'done' ? completed(person) : !completed(person);
  const largestGroup = Math.max(people.filter(completed).length, people.filter(person => !completed(person)).length, 1);
  const pendingKey = { filled: 'notFilled', opened: 'notOpened', revealed: 'notRevealed', sent: 'notSent', received: 'notReceived' }[status];
  const doneKey = participation ? 'filled' : status;
  const selectable = people.filter(person => person.opened && matches(person, filter) && cards.some(card => card.id === person.id && !card.isYou) && !box.isArchived);
  const canSelect = !box.isArchived && people.some(person => cards.some(card => card.id === person.id && !card.isYou));
  const selectedPeople = selectable.filter(person => selected.includes(person.id));
  return <div className="progress-details">
    <div className="progress-details__content">
    <p className="progress-details__description">{t(`participantProgress.${participation ? 'filledDescription' : `${status}Description`}`)}</p>
    <div className="progress-details__count"><strong>{count} / {progress.total}</strong><span>{percent}%</span></div>
    <progress className="progress-details__bar" value={count} max={progress.total || 1} aria-label={t(`participantProgress.${doneKey}`)} />
    {!progress.activeDraw && !['opened', 'filled'].includes(status) && <p>{t('participantProgress.beforeDraw')}</p>}
    <div className="progress-details__filters" role="tablist" aria-label={t('participantProgress.statusFilter')}>
      {(['pending', 'done'] as const).map((tab, index) => <button type="button" role="tab" key={tab} id={`${id}-${tab}`} aria-controls={`${id}-people`} aria-selected={filter === tab} tabIndex={filter === tab ? 0 : -1} onClick={() => setFilter(tab)} onKeyDown={event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const next = event.key === 'Home' ? 'pending' : event.key === 'End' ? 'done' : index === 0 ? 'done' : 'pending';
          setFilter(next);
          document.getElementById(`${id}-${next}`)?.focus();
        }
      }}>{tab === 'done' && <CheckIcon />}{t(`participantProgress.${tab === 'pending' ? pendingKey : doneKey}`)} ({people.filter(person => matches(person, tab)).length})</button>)}
    </div>
    {canSelect && <div className="progress-details__selection"><SelectionHint disabled={!selectable.length}><Checkbox fit disabled={!selectable.length} label={t('bulkReminder.selectAll')} checked={selectable.length > 0 && selectable.every(person => selected.includes(person.id))} onCheckedChange={checked => setSelected(checked ? selectable.map(person => person.id) : [])} /></SelectionHint><span>{t('bulkReminder.selected', { count: selectedPeople.length })}</span></div>}
    <div className="progress-details__panel" style={{ height: `min(45vh, ${largestGroup * 100 + 24}px)` }} role="tabpanel" id={`${id}-people`} aria-labelledby={`${id}-${filter}`} tabIndex={0}>
      <ul className="progress-details__people">
        {people.filter(person => matches(person, filter)).map(person => {
          const card = cards.find(candidate => candidate.id === person.id);
          return <ProgressParticipantRow key={person.id} person={person} card={card} status={status} box={box} invitations={invitations} onPreview={onPreview} selected={selected.includes(person.id)} selectionDisabled={!person.opened} onSelect={!box.isArchived && card && !card.isYou ? checked => setSelected(current => checked ? [...current, person.id] : current.filter(id => id !== person.id)) : undefined} />;
        })}
      </ul>
      {!people.some(person => matches(person, filter)) && <p>{t(filter === 'done' ? participation ? 'participantProgress.nobodyFilledYet' : status === 'sent' ? 'participantProgress.nobodySentYet' : status === 'received' ? 'participantProgress.nobodyReceivedYet' : 'participantProgress.noParticipants' : 'participantProgress.noParticipants')}</p>}
    </div>
    </div>
    {canSelect && <div className="progress-details__reminder">{selectedPeople.length > 0 && <BulkReminderButton box={box} context={status} recipients={cards.filter(card => selectedPeople.some(person => person.id === card.id))} onSent={ids => setSelected(current => current.filter(id => !ids.includes(id)))} />}</div>}
  </div>;
}

export type Progress = {
  participants?: ProgressParticipant[];
  total: number;
  engagement: { notOpened: number; opened: number; filled: number };
  claimed: number;
  revealed: number;
  sent: number;
  received: number;
  activeDraw: boolean;
};

export const LOCKED_PROGRESS: Progress = { total: 0, engagement: { notOpened: 0, opened: 0, filled: 0 }, claimed: 0, revealed: 0, sent: 0, received: 0, activeDraw: false };

export function ParticipantProgress({ progress, children, cards, box, preview = false, readOnly = false }: { preview?: boolean; readOnly?: boolean; progress: Progress; children?: ReactNode; cards: CardInfo[]; box: BoxInfo['box'] }) {
  const tableFilter = useParticipantProgressFilter();
  const setFilterPeople = tableFilter?.setPeople;
  useEffect(() => {
    if (!preview && progress.participants) setFilterPeople?.(progress.participants);
  }, [preview, progress.participants, setFilterPeople]);
  const { t } = useTranslation();
  const openParticipantPreview = useParticipantPreview();
  const previewCard = (card: CardInfo) => openParticipantPreview({ card, people: cards, box, isActiveDraw: progress.activeDraw, index: cards.findIndex(person => person.id === card.id), copiedIds: new Set<number>() }, true);
  const chartGrid = useRef<HTMLDivElement>(null);
  const [connectors, setConnectors] = useState<string[]>([]);
  useLayoutEffect(() => {
    const grid = chartGrid.current;
    if (!grid) return;
    const update = () => {
      const origin = grid.getBoundingClientRect();
      const cards = Array.from(grid.querySelectorAll<HTMLElement>('.participant-progress__summary')).map(card => card.getBoundingClientRect());
      setConnectors(cards.slice(1).map((next, index) => {
        const previous = cards[index];
        if (Math.abs(previous.top - next.top) < 1) {
          return `M ${previous.right - origin.left} ${previous.top + previous.height / 2 - origin.top} H ${next.left - origin.left}`;
        }
        const x1 = previous.left + previous.width / 2 - origin.left;
        const x2 = next.left + next.width / 2 - origin.left;
        const y1 = previous.bottom - origin.top;
        const y2 = next.top - origin.top;
        return `M ${x1} ${y1} V ${(y1 + y2) / 2} H ${x2} V ${y2}`;
      }));
    };
    const observer = new ResizeObserver(update);
    observer.observe(grid);
    grid.querySelectorAll('.participant-progress__summary').forEach(card => observer.observe(card));
    update();
    return () => observer.disconnect();
  }, [progress.total]);
  const tooltipId = useId();
  const [hintStep, setHintStep] = useState<string | null>(null);
  const engagement = progress.engagement ?? { notOpened: progress.total - progress.claimed, opened: progress.claimed, filled: 0 };
  const ring = (value: number, label: string) => (
    <div
      className="participant-progress__ring"
      role="img"
      aria-label={preview ? t('participantProgress.lockedPreview') : `${label}: ${value} / ${progress.total}`}
      style={{
        background: `conic-gradient(var(--color-statistics-gift-sent) ${preview ? 90 : progress.total ? (value / progress.total) * 360 : 0}deg, var(--color-statistics-gift-pending) 0)`,
      }}
    >
      <div>
        <strong>
          {preview ? <LockIcon /> : <><span className="participant-progress__ring-percent">{progress.total ? Math.round(value / progress.total * 100) : 0}%</span><span className="participant-progress__ring-count" style={{ fontSize: String(value).length + String(progress.total).length > 4 ? '11px' : undefined }}>{value}/{progress.total}</span></>}
        </strong>
      </div>
    </div>
  );
  return (
    <section
      className={`participant-progress${preview ? ' participant-progress--preview' : ''}${readOnly ? ' participant-progress--read-only' : ''}`}
      aria-label={t('participantProgress.title')}
      data-testid="participant-progress"
    >
      {!preview && progress.total === 0 ? (
        <p>{t('participantProgress.empty')}</p>
      ) : (
        <div className="participant-progress__details" ref={chartGrid}>
          <svg className="participant-progress__connections" aria-hidden="true">{connectors.map((path, index) => <path key={index} d={path} />)}</svg>
          {(['opened', 'filled', 'revealed', 'sent', 'received'] as const).map(status => {
            const locked = !preview && !['opened', 'filled'].includes(status) && !progress.activeDraw;
            const chartKey = `chart-${status}`;
            const showHint = locked && hintStep === chartKey;
            const count = status === 'opened' ? engagement.opened : status === 'filled' ? engagement.filled : progress[status];
            const complete = !preview && !locked && count === progress.total;
            const started = !preview && !locked && count > 0 && !complete;
            const title = t(`participantProgress.${status === 'opened' ? 'joined' : status === 'filled' ? 'filledCard' : status}`);
            return <button type="button" className={`participant-progress__summary${complete ? ' participant-progress__step--complete' : started ? ' participant-progress__step--started' : ''}`} key={status} disabled={preview || readOnly} aria-disabled={locked || undefined}
              aria-describedby={showHint ? `${tooltipId}-${chartKey}` : undefined}
              onMouseEnter={locked ? () => setHintStep(chartKey) : undefined}
              onMouseLeave={() => setHintStep(null)}
              onFocus={locked ? () => setHintStep(chartKey) : undefined}
              onBlur={() => setHintStep(null)}
              onKeyDown={event => { if (event.key === 'Escape' && locked) { event.stopPropagation(); setHintStep(null); } }}
              onClick={() => { if (preview || readOnly) return; if (locked) { setHintStep(chartKey); return; } if (tableFilter) { const people = progress.participants ?? []; const done = people.filter(person => person[status]).length; tableFilter.setFilter({ status, side: 'done', done, pending: people.length - done }); return; } showSheet({ title, children: <ProgressDetails progress={progress} status={status} cards={cards} box={box} onPreview={previewCard} />, testId: 'progress-details' }); }}>
              <div className="participant-progress__summary-metric">{ring(count, title)}<strong className="participant-progress__summary-count">{preview ? '— / —' : `${count}/${progress.total}`}</strong></div>
              <span className="participant-progress__summary-copy">
                <strong className="participant-progress__summary-title"><span aria-hidden="true">{status === 'opened' ? <UsersIcon /> : <ActivityEventIcon type={status === 'filled' ? 'participant.card_claimed' : status === 'revealed' ? 'participant.giftee_revealed' : status === 'sent' ? 'gift.sent' : 'gift.received'} />}</span>{title}</strong>
                <span>{t(`participantProgress.${status}Description`)}</span>
              </span>
              {showHint && <span role="tooltip" id={`${tooltipId}-${chartKey}`} className="participant-progress__tooltip">{t('participantProgress.drawNotConducted')}</span>}
              {complete && <span className="participant-progress__check" aria-hidden="true"><CheckIcon /></span>}

            </button>;
          })}
        </div>
      )}
      <div className="participant-progress__bottom">
          {children}
      </div>
    </section>
  );
}
